import { useCallback, useState } from 'react';
import { resendSignUpCode } from 'aws-amplify/auth';

export const UNCONFIRMED_USER_MESSAGE = 'Please check your email for a confirmation link';

export type ResendConfirmationResult = {
  success: boolean;
  message: string;
};

export async function resendConfirmationEmail(email: string): Promise<ResendConfirmationResult> {
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail) {
    return { success: false, message: 'Please enter a valid email address.' };
  }

  try {
    await resendSignUpCode({ username: normalizedEmail });
    return { success: true, message: 'Confirmation email sent. Please check your inbox.' };
  } catch (err: unknown) {
    const error = err as { name?: string; message?: string };
    if (error?.name === 'InvalidParameterException' && error?.message?.includes('CONFIRMED')) {
      return { success: false, message: 'Your account is already confirmed. You can log in now.' };
    }
    if (error?.name === 'UserNotFoundException') {
      return { success: false, message: 'No account found with this email address.' };
    }
    if (error?.name === 'LimitExceededException') {
      return { success: false, message: 'Too many attempts. Please wait a few minutes and try again.' };
    }
    return {
      success: false,
      message: error?.message || 'Failed to resend confirmation email. Please contact info@disgenet.com',
    };
  }
}

export function useResendConfirmationEmail(email: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const resend = useCallback(async () => {
    setIsLoading(true);
    setFeedback(null);
    const result = await resendConfirmationEmail(email);
    setFeedback({ type: result.success ? 'success' : 'error', message: result.message });
    setIsLoading(false);
  }, [email]);

  return { isLoading, feedback, resend };
}
